import type { SessionLifecycleNavigation } from '@core/ports/SessionLifecycleNavigation';
import { routes, type RouteDefinition } from '@infrastructure/router/routes';

export class RouterService implements SessionLifecycleNavigation {
  private outlet?: HTMLElement;

  private renderSequence = 0;

  private static readonly authenticationFailureReason = 'authentication-failed';

  public constructor(
    private readonly routeDefinitions: RouteDefinition[] = routes,
    private readonly getForcedPath?: () => string | undefined
  ) {}

  public start(outlet: HTMLElement): void {
    this.outlet = outlet;
    globalThis.addEventListener('popstate', this.handleNavigation);
    void this.render(globalThis.location.pathname);
  }

  public navigate(path: string): void {
    const resolvedPath = this.getResolvedPath(path);
    globalThis.history.pushState({}, '', resolvedPath);
    void this.render(resolvedPath);
  }

  public navigateToExpiredSession(): void {
    if (this.shouldPreserveAuthenticationFailureRoute()) {
      return;
    }

    if (globalThis.location.pathname === '/session-expired') {
      return;
    }

    this.navigate('/session-expired');
  }

  private readonly handleNavigation = (): void => {
    void this.render(globalThis.location.pathname);
  };

  private async render(path: string): Promise<void> {
    if (!this.outlet) {
      return;
    }

    const renderSequence = ++this.renderSequence;
    const resolvedPath = this.getResolvedPath(path);
    if (resolvedPath !== path) {
      globalThis.history.replaceState({}, '', resolvedPath);
    }

    const route = this.routeDefinitions.find((definition) => definition.path === resolvedPath) ?? this.routeDefinitions[0];
    document.title = route.title;

    const page = route.load
      ? await route.load()
      : document.createElement(route.tagName ?? 'div');

    if (!this.outlet || renderSequence !== this.renderSequence) {
      return;
    }

    this.outlet.replaceChildren(page);
    globalThis.dispatchEvent(new CustomEvent('inav:route-changed', {
      detail: {
        path: route.path,
      },
    }));
  }

  private getResolvedPath(path: string): string {
    if (this.shouldPreserveAuthenticationFailureRoute()) {
      return path;
    }

    return this.getForcedPath?.() ?? path;
  }

  private shouldPreserveAuthenticationFailureRoute(): boolean {
    const currentUrl = new URL(globalThis.location.href);
    return currentUrl.pathname === '/access-denied'
      && currentUrl.searchParams.get('reason') === RouterService.authenticationFailureReason;
  }
}